import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Container, Eyebrow, Mono, Section, SectionTitle } from "@/components/site/primitives";
import { cn } from "@/lib/utils";

const Node = ({ to, state, label, note, testid, className }) => (
  <Link to={to} state={state} data-testid={testid}
    className={cn("group flex items-start justify-between gap-4 border border-o-line bg-o-surface px-4 py-3 transition-[border-color,background-color] duration-200 hover:border-o-accent", className)}>
    <span>
      <span className="block font-display text-[15px] font-bold leading-tight">{label}</span>
      {note && <span className="mt-0.5 block text-xs text-o-dim">{note}</span>}
    </span>
    <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-o-dim transition-colors group-hover:text-o-accent" />
  </Link>
);

const Cluster = ({ label, dashed, children }) => (
  <div className={cn("p-3", dashed ? "border border-dashed border-o-strong" : "border border-o-line bg-o-raise")}>
    <Mono>{label}</Mono>
    <div className="mt-3 space-y-2">{children}</div>
  </div>
);

const Operation = ({ origin, title, begins, to, testid, className, children }) => (
  <div data-testid={testid} className={cn("flex flex-col border border-o-strong bg-o-bg p-5 sm:p-7", className)}>
    <div className="flex items-center justify-between gap-3">
      <Mono accent>{origin}</Mono>
      <Link to={to} data-testid={`${testid}-open`} className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-o-dim hover:text-o-accent">Open →</Link>
    </div>
    <h3 className="mt-4 font-display text-2xl font-bold sm:text-3xl">{title}</h3>
    <p className="mt-2 max-w-md text-sm text-o-dim">{begins}</p>
    <div className="mt-6 flex-1 space-y-3">{children}</div>
  </div>
);

const Connector = ({ className }) => (
  <div aria-hidden className={cn("flex flex-col items-center", className)}>
    <span className="h-7 w-px bg-o-text" />
    <Mono className="my-1.5">draws from</Mono>
    <span className="h-4 w-px bg-o-text" />
  </div>
);

const FoundationCell = ({ to, title, text, testid, className }) => (
  <Link to={to} data-testid={testid} className={cn("group flex items-start justify-between gap-6 p-6 transition-colors hover:bg-o-surface sm:p-8", className)}>
    <span>
      <span className="block font-display text-2xl font-bold sm:text-3xl">{title}</span>
      <span className="mt-2 block max-w-md text-sm leading-relaxed text-o-dim">{text}</span>
    </span>
    <ArrowUpRight className="h-5 w-5 shrink-0 text-o-dim transition-colors group-hover:text-o-accent" />
  </Link>
);

const DiagnosticsBlock = () => (
  <Operation className="lg:col-span-7" testid="map-diagnostics-software" to="/enterprise" origin="Customer-originated"
    title="Diagnostics & Software" begins="Begins with a customer's actual organizational situation and what needs to change.">
    <Node to="/enterprise/diagnostic" label="Ordo Diagnostic" note="Understand → Diagnose → Build → Connect → Improve" testid="map-diagnostic" />
    <div className="grid gap-3 sm:grid-cols-2">
      <Cluster label="Ordo software family">
        <Node to="/ordo/engine" state={{ via: "software" }} label="Ordo Engine" note="Present judgment" testid="map-engine" />
        <Node to="/ordo/stewardship" state={{ via: "software" }} label="Ordo Stewardship" note="Continuing standing" testid="map-stewardship" />
      </Cluster>
      <Cluster label="Independent offerings" dashed>
        <Node to="/enterprise/breakpoint" label="Breakpoint" note="Consequential recognition" testid="map-breakpoint" />
        <Node to="/enterprise/mesh" label="Mesh" note="Connected participation" testid="map-mesh" />
      </Cluster>
    </div>
    <p className="pt-1 text-xs text-o-dim">Also delivers architectural advisory, implementation, and continuing support.</p>
  </Operation>
);

const ResearchBlock = () => (
  <Operation className="lg:col-span-5" testid="map-research-products" to="/research" origin="Company-originated"
    title="Research & Products" begins="Begins with independent research into human needs and possibilities.">
    <Node to="/products/briefcase" label="Briefcase" note="Leadership and direction" testid="map-briefcase" />
    <Node to="/products/forma" label="Forma" note="Personally formed intelligence" testid="map-forma" />
    <Node to="/products/lumen" label="Lumen" note="Learning and the classroom" testid="map-lumen" />
    <Node to="/research#physical-breakpoint" label="Physical Breakpoint" note="Supporting example of product formation" testid="map-physical-breakpoint" className="border-dashed bg-transparent" />
  </Operation>
);

export const FoundationMap = () => (
  <Section id="foundation" theme="t-paper" testid="home-foundation" className="overflow-hidden">
    <div aria-hidden className="blueprint pointer-events-none absolute inset-0 opacity-50" />
    <Container className="relative py-20 lg:py-28">
      <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <Eyebrow index="01">The developed foundation</Eyebrow>
          <SectionTitle className="mt-5">One developed foundation. Two connected operations.</SectionTitle>
        </div>
        <p className="leading-relaxed text-o-dim lg:col-span-5">
          Diagnostics & Software begins with a customer's situation. Research & Products begins with independent research. Both draw from the same intellectual and technical foundation, and General Ordo also reaches outward as infrastructure.
        </p>
      </div>
      <div className="mt-14 grid gap-4 lg:grid-cols-12">
        <DiagnosticsBlock />
        <ResearchBlock />
      </div>
      <div className="grid lg:grid-cols-12">
        <Connector className="lg:col-span-7" />
        <Connector className="hidden lg:col-span-5 lg:flex" />
      </div>
      <div data-testid="map-foundation" className="t-ink bg-o-bg text-o-text">
        <div className="flex items-center justify-between gap-3 border-b border-o-line px-6 py-3 sm:px-8">
          <Mono accent>Shared intellectual & technical foundation</Mono>
          <Mono className="hidden sm:inline">Beneath both operations</Mono>
        </div>
        <div className="grid md:grid-cols-2">
          <FoundationCell to="/research#human-structure" testid="map-human-structure" title="Human Structure"
            text="How people, responsibilities, technology, knowledge, authority, and intelligence are arranged around human purposes." />
          <FoundationCell to="/ordo" testid="map-general-ordo" className="border-t border-o-line md:border-l md:border-t-0" title="General Ordo"
            text="The governing architecture for how intelligence understands, judges, acts, and remains answerable." />
        </div>
      </div>
      <div className="md:ml-[50%]">
        <div aria-hidden className="ml-10 h-8 border-l border-dashed border-o-text" />
        <Link to="/ordo/infrastructure" data-testid="map-infrastructure"
          className="group flex items-start justify-between gap-4 border border-dashed border-o-text px-5 py-4 transition-colors hover:bg-o-surface">
          <span>
            <Mono>Separate licensing route</Mono>
            <span className="mt-2 block font-display text-xl font-bold">General Ordo Infrastructure</span>
            <span className="mt-1 block text-sm text-o-dim">The architecture incorporated into intelligent systems developed by other organizations. Not a fifth applied product.</span>
          </span>
          <ArrowUpRight className="h-5 w-5 shrink-0 text-o-dim transition-colors group-hover:text-o-accent" />
        </Link>
      </div>
    </Container>
  </Section>
);
